import React, { memo, useMemo } from "react";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import { useTranslation } from "react-i18next";

import colours from "../../theme/colours";
import { getPrimaryColour } from "../../utils/getPrimaryColour";

ChartJS.register(...registerables);

function readFiniteNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function normaliseDetrendedPoints(detrendedPoints, timesSeconds) {
  if (!Array.isArray(detrendedPoints)) return [];

  return detrendedPoints
    .map((point, index) => {
      if (typeof point === "number") {
        const t = Array.isArray(timesSeconds)
          ? readFiniteNumber(timesSeconds[index])
          : index / 30;

        return {
          t,
          value: readFiniteNumber(point),
        };
      }

      if (point && typeof point === "object") {
        return {
          t: readFiniteNumber(point.t ?? point.time ?? point.x),
          value: readFiniteNumber(point.value ?? point.y),
        };
      }

      return null;
    })
    .filter((point) => point && point.t != null && point.value != null);
}

function buildCumulativePeakHeightSeries(points) {
  if (!Array.isArray(points) || points.length < 3) return [];

  let cumulativePeakHeight = 0;
  const series = [];

  for (let i = 1; i < points.length - 1; i += 1) {
    const previous = points[i - 1].value;
    const current = points[i].value;
    const next = points[i + 1].value;

    const isPeak = current > previous && current >= next;

    if (isPeak) {
      cumulativePeakHeight += Math.max(0, current);

      series.push({
        x: points[i].t,
        y: cumulativePeakHeight,
      });
    }
  }

  return series;
}

const PostExertionCumulativePeakHeightChart = memo(
  function PostExertionCumulativePeakHeightChart({
    detrendedPoints = [],
    timesSeconds = [],
    height = 240,
    maxSecs = 60,
  }) {
    const { t } = useTranslation();

    const cumulativePeakHeightPoints = useMemo(() => {
      const points = normaliseDetrendedPoints(detrendedPoints, timesSeconds);
      return buildCumulativePeakHeightSeries(points);
    }, [detrendedPoints, timesSeconds]);

    const data = useMemo(
      () => ({
        datasets: [
          {
            label: t(
              "result.postExertion.cumulativePeakHeightLabel",
              "Cumulative Peak Height",
            ),
            data: cumulativePeakHeightPoints,
            parsing: false,
            borderColor: getPrimaryColour(),
            backgroundColor: "transparent",
            pointRadius: 0,
            pointHoverRadius: 0,
            hitRadius: 8,
            borderWidth: 2,
            tension: 0.15,
            stepped: true,
          },
        ],
      }),
      [cumulativePeakHeightPoints, t],
    );

    const options = useMemo(
      () => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "nearest",
          intersect: false,
        },
        plugins: {
          legend: {
            display: true,
            labels: {
              color: colours.typography,
              usePointStyle: true,
              boxWidth: 10,
            },
          },
          tooltip: {
            callbacks: {
              title(items) {
                const x = items?.[0]?.parsed?.x;

                return typeof x === "number" && Number.isFinite(x)
                  ? t("result.postExertion.decayTooltipTitle", {
                      defaultValue: "{{seconds}} s",
                      seconds: x.toFixed(1),
                    })
                  : "";
              },
              label(item) {
                const y = item?.parsed?.y;

                return typeof y === "number" && Number.isFinite(y)
                  ? t("result.postExertion.cumulativePeakHeightTooltipLabel", {
                      defaultValue: "Cumulative peak height: {{value}}",
                      value: y.toFixed(3),
                    })
                  : "";
              },
            },
          },
        },
        scales: {
          x: {
            type: "linear",
            min: 0,
            max: maxSecs,
            title: {
              display: true,
              text: t("charts.signal.timeSeconds", "Time (s)"),
              color: colours.typography,
            },
            grid: {
              color: "rgba(147,147,147,0.2)",
            },
            ticks: {
              color: colours.typography,
            },
          },
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: t(
                "result.postExertion.cumulativePeakHeightYAxis",
                "Cumulative peak height",
              ),
              color: colours.typography,
            },
            grid: {
              color: "rgba(147,147,147,0.15)",
            },
            ticks: {
              color: colours.typography,
            },
          },
        },
      }),
      [maxSecs, t],
    );

    return (
      <div style={{ height }}>
        <Line data={data} options={options} />
      </div>
    );
  },
);

export default PostExertionCumulativePeakHeightChart;
